import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get state safely
  const state = location.state || {};
  const {
    fullName = "",
    username = "",
    email = "",
    phone = "",
    dob = "",
    gender = "",
    description = "",
    profession = "",
    selectedCategory = "",
    categoryPath = [],
    profilePic = null,
    registration = "",
    city = "",
    district = "",
    state: userState = "",
    country = "",
    postalCode = "",
    googleMapLink = "",
    village = "",
    amount = 200, // default amount
    userId = "",  // pass userId if available
  } = state;

  const [mobile, setMobile] = useState(phone || "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Check if user is new or has subscription
  useEffect(() => {
    if (!fullName || !username || !email) {
      navigate("/register");
      return;
    }

    const checkUserSubscription = async () => {
      if (!userId) return;

      try {
        const res = await fetch(`/api/subscriptionStatus/${userId}`);
        const data = await res.json();

        if (data.isNewUser) {
          setIsNewUser(true);
          setTrialInfo({ start: data.trialStartDate, end: data.trialEndDate });
        } else {
          setIsNewUser(false);
          setSubscriptionStatus(data.subscriptionStatus);
          setTrialInfo({ start: data.trialStartDate, end: data.trialEndDate });
        }
      } catch (err) {
        console.error("Error fetching subscription status:", err);
      }
    };

    checkUserSubscription();
  }, [fullName, username, email, navigate, userId]);

  const handlePayment = async () => {
    if (!mobile || mobile.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const paymentResponse = await fetch("/api/startTrialOrPayment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          plan: "1_month", // default or selected plan
        }),
      });

      const data = await paymentResponse.json();

      if (data.isNewUser) {
        setSuccess(`Trial started! Ends on ${new Date(data.trialEndDate).toLocaleDateString()}`);
        setIsNewUser(true);
        setTrialInfo({ start: data.trialStartDate, end: data.trialEndDate });
      } else if (data.paymentUrl) {
        // Redirect to PhonePe checkout
        window.location.href = data.paymentUrl;
      } else {
        setError(data.message || "Unable to start trial or generate payment link.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong while initiating payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        padding: "20px",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          padding: "40px 30px",
          borderRadius: "16px",
          boxShadow: "0 15px 40px rgba(0,0,0,0.15)",
          maxWidth: "500px",
          width: "100%",
          textAlign: "center",
          transition: "all 0.3s ease",
        }}
      >
        <h2 style={{ marginBottom: "25px", color: "#333", fontSize: "24px" }}>
          {isNewUser ? "Start Your Trial" : "Complete Your Payment"}
        </h2>

        <div
          style={{
            textAlign: "left",
            marginBottom: "25px",
            fontSize: "15px",
            lineHeight: "1.6",
            color: "#555",
          }}
        >
          <p><strong>Full Name:</strong> {fullName}</p>
          <p><strong>Username:</strong> {username}</p>
          <p><strong>Email:</strong> {email}</p>
          <p><strong>Phone:</strong> {mobile}</p>
          <p><strong>Profession:</strong> {profession}</p>
          <p><strong>Category:</strong> {selectedCategory}</p>
          <p><strong>Description:</strong> {description}</p>
          {!isNewUser && <p><strong>Amount to Pay:</strong> ₹{amount}</p>}
          {isNewUser && trialInfo && (
            <p>
              <strong>Trial:</strong> {new Date(trialInfo.start).toLocaleDateString()} -{" "}
              {new Date(trialInfo.end).toLocaleDateString()}
            </p>
          )}
        </div>

        {!isNewUser && (
          <input
            type="tel"
            placeholder="Enter mobile number"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            style={{
              padding: "12px",
              width: "100%",
              marginBottom: "20px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          />
        )}

        {error && <p style={{ color: "red", marginBottom: "10px" }}>{error}</p>}
        {success && <p style={{ color: "green", marginBottom: "10px" }}>{success}</p>}

        <button
          onClick={handlePayment}
          disabled={loading}
          style={{
            width: "100%",
            padding: "14px 0",
            backgroundColor: "#00bfff",
            color: "white",
            fontWeight: "bold",
            border: "none",
            borderRadius: "10px",
            cursor: "pointer",
            fontSize: "16px",
            marginBottom: "20px",
            transition: "background 0.3s ease",
          }}
        >
          {loading
            ? "Processing..."
            : isNewUser
            ? "Start Trial"
            : "Pay with PhonePe"}
        </button>

        {!isNewUser && mobile && (
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <img
              src="/updated.jpg"
              alt="PhonePe QR Code"
              style={{ width: 180, height: 180, borderRadius: "12px" }}
            />
            <p style={{ marginTop: "10px", fontWeight: "bold", color: "#333" }}>
              OR Scan this PhonePe QR Code to pay
            </p>
            <p style={{ color: "#555" }}>Rajula Guru Kumar Reddy</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
